import {
  StatusIndicatorIcon,
  Typography,
} from "@fielmann-ag/prisma-components";

import { useI18n } from "@/i18n";
import { joinClassNames } from "@/lib/utils";
import { getPasswordRequirementStatus } from "@/validation";

interface PasswordRequirementsProps {
  password: string;
  showErrors?: boolean;
  /** Referenced by the password input's `aria-describedby`. */
  id?: string;
  /** Extra classes for the outer list. */
  className?: string;
}

type RequirementVisualState = "success" | "error" | "neutral";

function getRequirementVisualState(
  isInformational: boolean,
  isMet: boolean,
  hasPasswordInput: boolean,
  showErrors: boolean,
): RequirementVisualState {
  if (isInformational) {
    return isMet ? "success" : "neutral";
  }

  if (hasPasswordInput) {
    return isMet ? "success" : "error";
  }

  if (showErrors && !isMet) {
    return "error";
  }

  return "neutral";
}

const requirementVisualStyles: Record<
  RequirementVisualState,
  { color: "secondary" | "inherit"; className?: string }
> = {
  success: {
    color: "inherit",
    className: "text-success-primary",
  },
  error: {
    color: "inherit",
    className: "text-error-primary",
  },
  neutral: {
    color: "secondary",
  },
};

function RequirementMarker({
  visualState,
  color,
  className,
}: {
  visualState: RequirementVisualState;
  color: "secondary" | "inherit";
  className?: string;
}) {
  if (visualState === "success") {
    return (
      <StatusIndicatorIcon
        status="positive"
        size={50}
        aria-hidden="true"
        className="shrink-0"
      />
    );
  }

  if (visualState === "error") {
    return (
      <StatusIndicatorIcon
        status="negative"
        size={50}
        aria-hidden="true"
        className="shrink-0"
      />
    );
  }

  // Match StatusIndicatorIcon size 50 (16px) so labels share one text column.
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-1 w-1 shrink-0 items-center justify-center"
    >
      <Typography
        as="span"
        variant="label"
        size={50}
        weight="regular"
        color={color}
        className={className}
      >
        •
      </Typography>
    </span>
  );
}

/**
 * Always-visible password policy helper shown below the password field.
 * Shared by signup and reset-password (same Auth0 DB connection policy).
 */
export function PasswordRequirements({
  password,
  showErrors = false,
  id,
  className,
}: PasswordRequirementsProps) {
  const { messages } = useI18n();
  const requirements = getPasswordRequirementStatus(
    password,
    messages.passwordRequirements,
  );
  const hasPasswordInput = password.length > 0;

  return (
    <ul id={id} className={joinClassNames("flex flex-col gap-0.25", className)}>
      {requirements.map((requirement) => {
        const isInformational = requirement.isInformational === true;
        const visualState = getRequirementVisualState(
          isInformational,
          requirement.isMet,
          hasPasswordInput,
          showErrors,
        );
        const { color, className: textClassName } =
          requirementVisualStyles[visualState];

        return (
          <li key={requirement.id} className="flex items-center gap-0.25">
            <RequirementMarker
              visualState={visualState}
              color={color}
              className={textClassName}
            />
            <Typography
              as="span"
              variant="label"
              size={50}
              weight="regular"
              color={color}
              className={textClassName}
            >
              {requirement.label}
            </Typography>
          </li>
        );
      })}
    </ul>
  );
}
